import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "@fortawesome/fontawesome-free/css/all.min.css";
import { openCreateGifteeWizard } from "../createGifteeWizardNav.js";
import { useGiftees } from "../hooks/useGiftees";
import { useAllReminders } from "../hooks/useAllReminders";
import { useSavedRecommendations } from "../hooks/useSavedRecommendations";
import styles from "../styles/GifteeAllOccasionsScreen.module.css";
import GifteeAppBottomNav from "../components/GifteeAppBottomNav.jsx";
import GifteeEventRow from "../components/GifteeEventRow.jsx";

function startOfDayMs(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x.getTime();
}

function reminderDayMs(reminder) {
  if (!reminder?.date) return null;
  const d =
    typeof reminder.date?.toDate === "function"
      ? reminder.date.toDate()
      : new Date(reminder.date);
  if (Number.isNaN(d.getTime())) return null;
  return startOfDayMs(d);
}

function daysUntil(ts) {
  if (!ts) return null;
  const dateObj = ts.toDate ? ts.toDate() : new Date(ts);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const target = new Date(dateObj);
  target.setHours(0, 0, 0, 0);
  const diff = Math.ceil((target - now) / 86400000);
  if (diff < 0) {
    const nextYear = new Date(
      now.getFullYear() + 1,
      target.getMonth(),
      target.getDate(),
    );
    return Math.ceil((nextYear - now) / 86400000);
  }
  return diff;
}

function dateLabel(ts) {
  if (!ts) return "";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { month: "numeric", day: "numeric" });
}

function occasionTitle(reminder) {
  if (typeof reminder?.title === "string" && reminder.title.trim()) {
    return reminder.title.trim();
  }
  if (typeof reminder?.type === "string" && reminder.type.trim()) {
    return reminder.type
      .split(/[-_]/g)
      .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
      .join(" ");
  }
  return "Occasion";
}


function SharedCelebrationCard({ group, gifteeById, isExpanded, onToggle, navigate }) {
  const gifteeCount = group.rows.length;
  const daysLabel = daysUntil(group.date);
  const daysText = daysLabel != null && Number.isFinite(daysLabel)
    ? `${daysLabel} days`
    : "—";

  const giftees = group.rows
    .map((row) => gifteeById.get(row.gifteeId))
    .filter(Boolean);

  return (
    <div className={`${styles.sharedCelebrationCard} ${isExpanded ? styles.sharedCelebrationCardExpanded : ""}`}>
      <div className={styles.celebrationHeader} data-header>
        <h3 className={styles.celebrationTitle}>{group.title}</h3>
        <button
          type="button"
          className={`${styles.expandBtn} ${styles.sharedExpandBtn}`}
          onClick={onToggle}
          aria-label={isExpanded ? `Collapse ${group.title}` : `Expand ${group.title}`}
        >
          {isExpanded ? (
            <i className="fa-solid fa-down-left-and-up-right-to-center" />
          ) : (
            <i className="fa-solid fa-up-right-and-down-left-from-center" />
          )}
        </button>
      </div>
      <div className={styles.celebrationTags}>
        <div className={styles.tagGiftees}>
          {gifteeCount} Giftee{gifteeCount !== 1 ? 's' : ''}
        </div>
        <div className={styles.tagDate}>
          {dateLabel(group.date)}
        </div>
        <div className={styles.tagDays}>
          {daysText}
        </div>
      </div>
      {isExpanded && giftees.length > 0 && (
        <div className={styles.sharedGifteeList}>
          {giftees.map((giftee) => (
            <GifteeEventRow
              key={giftee.id}
              giftee={giftee}
              navigate={navigate}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function IndividualCelebrationCard({ reminder, giftee, navigate }) {
  const { savedRecommendations, loading } = useSavedRecommendations(giftee.id);
  const recCount = loading
    ? 0
    : savedRecommendations.length > 0
      ? savedRecommendations.length
      : 2;

  return (
    <div className={styles.individualCelebrationCard}>
      <div className={styles.celebrationHeader}>
        <h3 className={styles.celebrationTitle}>{occasionTitle(reminder)}</h3>
        <button
          type="button"
          className={styles.expandBtn}
          onClick={() => navigate(`/giftee/${giftee.id}`)}
          aria-label={`View ${giftee.name || 'giftee'}'s ${occasionTitle(reminder)}`}
        >
          <i className={`fa-solid fa-arrow-right ${styles.arrowIcon}`} />
        </button>
      </div>
      <div className={styles.celebrationTags}>
        <div className={styles.tagName}>
          {giftee.name || 'Giftee'}
        </div>
        <div className={styles.tagDate}>
          {dateLabel(reminder.date)}
        </div>
        <div className={styles.tagRecommendations}>
          {recCount} Recommendation{recCount !== 1 ? 's' : ''}
        </div>
      </div>
    </div>
  );
}

export default function GifteeAllOccasionsScreen() {
  const navigate = useNavigate();
  const { giftees, loading: gifteesLoading } = useGiftees();
  const { allReminders, loading: remindersLoading } = useAllReminders(giftees);
  const [expandedSharedKey, setExpandedSharedKey] = useState(null);

  useEffect(() => {
    if (gifteesLoading) return;
    if (giftees.length === 0) {
      openCreateGifteeWizard(navigate, "/all-occasions", { replace: true });
    }
  }, [gifteesLoading, giftees.length, navigate]);

  const gifteeById = useMemo(() => {
    const map = new Map();
    giftees.forEach((g) => map.set(g.id, g));
    return map;
  }, [giftees]);

  const grouped = useMemo(() => {
    const next = allReminders
      .map((reminder) => ({ reminder, dayMs: reminderDayMs(reminder) }))
      .filter((item) => item.dayMs !== null)
      .sort((a, b) => a.dayMs - b.dayMs);

    const byOccasion = new Map();
    next.forEach(({ reminder, dayMs }) => {
      const key = `${reminder.type || reminder.title || "occasion"}|${dayMs}`;
      const existing = byOccasion.get(key);
      if (existing) {
        existing.rows.push(reminder);
      } else {
        byOccasion.set(key, {
          key,
          title: occasionTitle(reminder),
          date: reminder.date,
          rows: [reminder],
        });
      }
    });

    const buckets = Array.from(byOccasion.values());
    const featured = buckets.filter((b) => b.rows.length >= 2);
    const singles = buckets.filter((b) => b.rows.length === 1);
    return { featured, singles };
  }, [allReminders]);

  if (gifteesLoading || remindersLoading) {
    return (
      <div className={styles.shell}>
        <div className={styles.loadingState}>Loading...</div>
      </div>
    );
  }

  if (!giftees.length) return null;

  const totalCelebrations = allReminders.length;

  return (
    <div className={styles.shell}>
      {/* Main Content - single scroll container; header is first child so sticky works */}
      <main className={styles.content}>
        {/* Header - sticky within the scroll container so backdrop-filter works */}
        <div className={styles.header}>
          <h1 className={styles.headerTitle}>All Celebrations</h1>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={() => navigate(-1)}
            aria-label="Close"
          >
            <i className="fa-solid fa-xmark" />
          </button>
        </div>

        {/* Title Section */}
        <div className={styles.titleSection}>
          <div className={styles.celebrationCount}>
            <div className={styles.countBox}>
              {totalCelebrations}
            </div>
            <div className={styles.countLabel}>Celebrations</div>
          </div>
          <button
            type="button"
            className={styles.addNewBtn}
          >
            Add New
            <i className="fa-solid fa-plus" />
          </button>
        </div>

        {/* Main Celebration Lists */}
        <div className={styles.celebrationsList}>
          {/* Shared Celebrations Section */}
          {grouped.featured.length > 0 && (
            <section className={styles.celebrationsSection}>
              <h2 className={styles.sectionTitle}>Shared Celebrations</h2>
              <div className={styles.sharedCelebrationsList}>
                {grouped.featured.map((group) => (
                  <SharedCelebrationCard
                    key={group.key}
                    group={group}
                    gifteeById={gifteeById}
                    isExpanded={expandedSharedKey === group.key}
                    onToggle={() =>
                      setExpandedSharedKey(
                        expandedSharedKey === group.key ? null : group.key,
                      )
                    }
                    navigate={navigate}
                  />
                ))}
              </div>
            </section>
          )}

          {/* Individual Celebrations Section */}
          {grouped.singles.length > 0 && (
            <section className={styles.celebrationsSection}>
              <h2 className={styles.sectionTitle}>Individual Celebrations</h2>
              <div className={styles.individualCelebrationsList}>
                {grouped.singles.map((group) => {
                  const reminder = group.rows[0];
                  const giftee = gifteeById.get(reminder.gifteeId);
                  if (!giftee) return null;
                  return (
                    <IndividualCelebrationCard
                      key={group.key}
                      reminder={reminder}
                      giftee={giftee}
                      navigate={navigate}
                    />
                  );
                })}
              </div>
            </section>
          )}

          {totalCelebrations === 0 && (
            <div className={styles.emptyState}>
              <p>No celebrations yet. Add some giftees to get started!</p>
            </div>
          )}
        </div>
      </main>
      <GifteeAppBottomNav />
    </div>
  );
}
